<template>
  <div class="fragment-form-container">
    <el-form 
      :model="form" 
      :rules="rules" 
      ref="fragmentForm" 
      label-width="120px"
      class="fragment-form"
    >
      <!-- 文件名称 + 价格（同一行） -->
      <div class="form-row">
        <el-form-item label="文件名称" prop="name" class="form-item-half">
          <el-input 
            v-model="form.name" 
            placeholder="请输入文件名称"
            clearable
            maxlength="100"
            show-word-limit
          />
        </el-form-item>
        <el-form-item label="价格" prop="price" class="form-item-half">
          <el-input-number 
            v-model="form.price" 
            :min="0" 
            :max="9999.99"
            :precision="2"
            placeholder="价格"
            style="width: 100%"
          />
        </el-form-item>
      </div>

      <!-- 简介 + 页数 -->
      <div class="form-row">
        <el-form-item label="简介" prop="description" class="form-item-half description-item">
          <el-input 
            v-model="form.description" 
            type="textarea" 
            :rows="3"
            placeholder="请输入简介"
            maxlength="500"
            show-word-limit
            class="description-textarea"
          />
        </el-form-item>
        <div class="form-item-half description-right-column">
          <el-form-item label="页数" prop="page" class="form-item-full">
            <el-input-number 
              v-model="form.page" 
              :min="1" 
              :max="9999"
              placeholder="页数"
              style="width: 100%"
            />
          </el-form-item>
          <el-form-item 
            v-if="mode === 'add'"
            label="是否精选" 
            prop="hot" 
            class="form-item-full"
          >
            <el-radio-group v-model="form.hot">
              <el-radio :label="0">否</el-radio>
              <el-radio :label="1">是</el-radio>
            </el-radio-group>
          </el-form-item>
          <template v-else>
            <div style="height: 32px;"></div>
          </template>
        </div>
      </div>

      <!-- PPT类型特有字段：分类（tag标签）、组别、动画 -->
      <template v-if="fileType === 1">
        <!-- 分类 + 作者 -->
        <div class="form-row">
          <el-form-item label="分类" prop="tagId" class="form-item-half">
            <el-select 
              v-model="form.tagId" 
              placeholder="请选择分类"
              style="width: 100%"
              clearable
            >
              <el-option
                v-for="tag in tagOptions"
                :key="tag.id"
                :label="tag.name"
                :value="tag.id"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="作者" prop="author" class="form-item-half">
            <el-input 
              v-model="form.author" 
              placeholder="请输入作者名称"
              clearable
              maxlength="50"
            />
          </el-form-item>
        </div>

        <!-- 组别 + 动画（仅新增模式） -->
        <div class="form-row" v-if="mode === 'add'">
          <el-form-item label="组别" prop="groupId" class="form-item-half">
            <el-select 
              v-model="form.groupId" 
              placeholder="请选择组别"
              style="width: 100%"
              clearable
            >
              <el-option
                v-for="group in groupOptions"
                :key="group.id"
                :label="group.name"
                :value="group.id"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="动画" prop="animation" class="form-item-half">
            <el-select 
              v-model="form.animation" 
              placeholder="请选择动画"
              style="width: 100%"
            >
              <el-option label="无动画" :value="0" />
              <el-option label="带路演版动画" :value="1" />
              <el-option label="带网评版动画" :value="2" />
            </el-select>
          </el-form-item>
        </div>
      </template>

      <!-- Word类型特有字段：分类（tag标签） -->
      <template v-else>
        <!-- 分类 + 作者 -->
        <div class="form-row">
          <el-form-item label="分类" prop="tagId" class="form-item-half">
            <el-select 
              v-model="form.tagId" 
              placeholder="请选择分类"
              style="width: 100%"
              clearable
            >
              <el-option
                v-for="tag in tagOptions"
                :key="tag.id"
                :label="tag.name"
                :value="tag.id"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="作者" prop="author" class="form-item-half">
            <el-input 
              v-model="form.author" 
              placeholder="请输入作者名称"
              clearable
              maxlength="50"
            />
          </el-form-item>
        </div>
      </template>

      <!-- 上传文件（与文件上传组件统一样式） -->
      <div class="form-row file-upload-row">
        <!-- 左侧：文件上传 -->
        <el-form-item label="上传文件" prop="file" class="form-item-half">
          <div class="upload-wrapper">
            <div class="upload-box">
              <el-upload
                ref="fileUpload"
                :action="uploadUrl"
                :auto-upload="false"
                :show-file-list="false"
                :file-list="fileList"
                :before-upload="beforeUpload"
                :on-change="onFileChange"
                :on-remove="onFileRemove"
                :limit="1"
                :accept="acceptedFileTypes"
                class="upload-dragger"
              >
                <div class="upload-content">
                  <div class="upload-inner">
                    <i class="el-icon-upload upload-icon"></i>
                    <div class="upload-tip-text">
                      支持 {{ acceptedFileTypes }} 格式<br>
                      文件大小不超过 100MB
                    </div>
                    <el-button 
                      type="primary" 
                      size="small" 
                      class="upload-btn"
                      @click.stop="handleUploadButtonClick"
                    >
                      {{ selectedFile ? '重新选择' : '选择文件' }}
                    </el-button>
                  </div>
                </div>
              </el-upload>
            </div>
          </div>
        </el-form-item>
        <!-- 右侧：文件信息展示 -->
        <el-form-item class="form-item-half file-info-item">
          <div class="file-info-card" :class="{ 'is-placeholder': !selectedFile }">
            <template v-if="selectedFile">
              <div class="file-info-name" :title="selectedFile.name">
                {{ selectedFile.name }}
              </div>
              <div class="file-info-meta">
                <span>大小：{{ (selectedFile.size / 1024 / 1024).toFixed(2) }} MB</span>
              </div>
              <div class="file-info-actions">
                <el-button 
                  size="small" 
                  type="primary" 
                  plain 
                  class="file-info-action" 
                  @click="handleUploadButtonClick($event)"
                >
                  重新选择
                </el-button>
                <span class="divider">|</span>
                <el-button 
                  size="small" 
                  type="danger" 
                  plain 
                  class="file-info-action" 
                  @click="onFileRemove"
                >
                  移除
                </el-button>
              </div>
            </template>
            <template v-else>
              <div class="file-info-placeholder-icon">
                <i class="el-icon-document"></i>
              </div>
              <div class="file-info-placeholder-text">尚未选择文件</div>
              <div class="file-info-placeholder-tip">请点击左侧选择需要上传的文件</div>
            </template>
          </div>
        </el-form-item>
      </div>

      <!-- 详情图（仅新增模式显示） -->
      <div class="form-row" v-if="mode === 'add'">
        <el-form-item label="详情图" prop="images" class="form-item-full">
          <el-upload
            ref="imageUpload"
            :action="uploadUrl"
            :auto-upload="false"
            :file-list="imageFileList"
            :before-upload="beforeImageUpload"
            :on-change="onImageChange"
            :on-remove="onImageRemove"
            :limit="10"
            :accept="acceptedImageTypes"
            :multiple="true"
            list-type="picture-card"
            class="image-upload"
          >
            <i class="el-icon-plus"></i>
          </el-upload>
          <div class="upload-tip">支持 JPG、PNG 格式，最多上传 10 张详情图，可多选</div>
        </el-form-item>
      </div>

      <!-- 备注 -->
      <div class="form-row">
        <el-form-item label="备注" prop="remark" class="form-item-full">
          <el-input 
            v-model="form.remark" 
            type="textarea" 
            :rows="3"
            placeholder="请输入备注信息（可选）"
            maxlength="500"
            show-word-limit
          />
        </el-form-item>
      </div>

      <!-- 按钮区域 -->
      <div class="form-actions">
        <el-button @click="handleCancel">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitting">
          {{ mode === 'add' ? '确定添加' : '确定修改' }}
        </el-button>
      </div>
    </el-form>
  </div>
</template>

<script>
import api from '@/api/api';

export default {
  name: 'FragmentForm',
  props: {
    // 表单模式：'add' 新增 或 'edit' 修改
    mode: {
      type: String,
      default: 'add',
      validator: (value) => ['add', 'edit'].includes(value)
    },
    // 文件类型：1 PPT, 2 Word
    fileType: {
      type: Number,
      default: 1
    },
    // 所属父文件编码（新增片段时必填）
    parentCode: {
      type: String,
      default: ''
    },
    // 片段数据（修改模式使用）
    fragmentData: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      submitting: false,
      form: {
        code: '',
        name: '',
        description: '',
        page: null,
        hot: 0,
        tagId: null,
        groupId: null,
        animation: null,
        price: null,
        author: '',
        remark: '',
        file: null
      },
      rules: {},
      fileList: [],
      imageFileList: [],
      selectedFile: null,
      selectedImageFiles: [],
      tagOptions: [],
      groupOptions: [],
      uploadUrl: process.env.VUE_APP_API_BASE_URL + '/fragment/upload',
      acceptedFileTypes: '.ppt,.pptx,.doc,.docx',
      acceptedImageTypes: '.jpg,.jpeg,.png'
    };
  },
  mounted() {
    this.initRules();
    this.initFormData();
    this.loadTagOptions();
    if (this.fileType === 1) {
      this.loadGroupOptions();
    }
  },
  watch: {
    fragmentData: {
      handler(newVal) {
        if (newVal && this.mode === 'edit') {
          this.initFormData();
        }
      },
      deep: true
    }
  },
  methods: {
    // 初始化验证规则
    initRules() {
      this.rules = {
        name: [
          { required: true, message: '请输入文件名称', trigger: 'blur' },
          { min: 2, max: 100, message: '文件名称长度在 2 到 100 个字符', trigger: 'blur' }
        ],
        description: [
          { required: true, message: '请输入简介', trigger: 'blur' },
          { max: 500, message: '简介长度不能超过 500 个字符', trigger: 'blur' }
        ],
        page: [
          { required: true, message: '请输入页数', trigger: 'blur' },
          { type: 'number', min: 1, max: 9999, message: '页数必须在 1 到 9999 之间', trigger: 'blur' }
        ],
        hot: [
          { required: true, message: '请选择是否精选', trigger: 'change' }
        ],
        tagId: [
          { required: true, message: '请选择分类', trigger: 'change' }
        ],
        groupId: [
          { validator: this.validateGroupId, trigger: 'change' }
        ],
        animation: [
          { validator: this.validateAnimation, trigger: 'change' }
        ],
        price: [
          { required: true, message: '请输入价格', trigger: 'blur' },
          { type: 'number', min: 0, max: 9999.99, message: '价格必须在 0 到 9999.99 之间', trigger: 'blur' }
        ],
        author: [
          { required: true, message: '请输入作者名称', trigger: 'blur' },
          { min: 2, max: 50, message: '作者名称长度在 2 到 50 个字符', trigger: 'blur' }
        ],
        remark: [
          { max: 500, message: '备注长度不能超过 500 个字符', trigger: 'blur' }
        ],
        file: [
          { validator: this.validateFile, trigger: 'change' }
        ]
      };
    },

    // 初始化表单数据
    initFormData() {
      if (this.mode === 'edit' && this.fragmentData) {
        // 修改模式：填充现有数据
        const data = this.fragmentData || {};
        this.form = {
          code: data.code || '',
          name: data.name || '',
          description: data.describes || data.description || '',
          page: data.pageCount !== undefined ? data.pageCount : (data.page || null),
          hot: data.hot !== undefined ? data.hot : 0,
          tagId: data.tag1Id !== undefined ? data.tag1Id : (data.tagId || null),
          groupId: data.tag2Id !== undefined ? data.tag2Id : (data.groupId || null),
          animation: data.animation !== undefined ? data.animation : null,
          price: data.price !== undefined ? data.price : null,
          author: data.author || '',
          remark: data.remark || '',
          file: null
        };
      } else {
        // 新增模式：重置表单
        this.form = {
          code: '',
          name: '',
          description: '',
          page: null,
          hot: 0,
          tagId: null,
          groupId: null,
          animation: null,
          price: null,
          author: '',
          remark: '',
          file: null
        };
      }
      
      // 重置文件列表
      this.fileList = [];
      this.imageFileList = [];
      this.selectedFile = null;
      this.selectedImageFiles = [];
      
      // 清除表单验证
      this.$nextTick(() => {
        if (this.$refs.fragmentForm) {
          this.$refs.fragmentForm.clearValidate();
        }
      });
    },

    // 加载标签选项
    async loadTagOptions() {
      try {
        const response = await api.getTagList({ mark: 1, tag: 1, keyword: '' });
        if (response.data && response.data.code === 200) {
          this.tagOptions = response.data.data || [];
        } else {
          this.tagOptions = [];
        }
      } catch (error) {
        console.error('加载标签选项失败:', error);
        this.tagOptions = [];
      }
    },

    // 加载组别选项
    async loadGroupOptions() {
      try {
        const response = await api.getTagList({ mark: 1, tag: 2, keyword: '' });
        if (response.data && response.data.code === 200) {
          this.groupOptions = response.data.data || [];
        } else {
          this.groupOptions = [];
        }
      } catch (error) {
        console.error('加载组别选项失败:', error);
        this.groupOptions = [];
      }
    },

    // 验证组别（仅PPT类型且新增模式必填）
    validateGroupId(rule, value, callback) {
      if (this.fileType === 1 && this.mode === 'add' && !value) {
        callback(new Error('请选择组别'));
      } else {
        callback();
      }
    },

    // 验证动画（仅PPT类型且新增模式必填）
    validateAnimation(rule, value, callback) {
      if (this.fileType === 1 && this.mode === 'add' && (value === null || value === undefined)) {
        callback(new Error('请选择动画'));
      } else {
        callback();
      }
    },

    // 验证文件
    validateFile(rule, value, callback) {
      if (!this.selectedFile && this.mode === 'add') {
        callback(new Error('请上传文件'));
      } else {
        callback();
      }
    },

    // 文件上传前处理
    beforeUpload(file) {
      const isValidType = this.acceptedFileTypes.split(',').some(type => 
        file.name.toLowerCase().endsWith(type.replace('.', ''))
      );
      
      if (!isValidType) {
        this.$message.error('文件格式不支持！');
        return false;
      }
      
      const isLt100M = file.size / 1024 / 1024 < 100;
      if (!isLt100M) {
        this.$message.error('文件大小不能超过 100MB！');
        return false;
      }
      
      return true;
    },

    // 文件选择变化
    onFileChange(file, fileList) {
      if (file.status === 'ready') {
        // 更新选中的文件对象，这会触发右侧文件信息的更新
        this.selectedFile = file.raw;
        // 自动填充文件信息（重新选择文件时总是更新）
        this.autoFillFromFile(file.raw);
      }
    },

    // 根据所选文件自动填充名称/简介（重新选择文件时总是更新）
    autoFillFromFile(file) {
      if (!file) return;
      const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
      // 重新选择文件时，总是更新文件名称和简介
      this.form.name = nameWithoutExt;
      this.form.description = nameWithoutExt;
    },

    // 文件移除
    onFileRemove() {
      this.selectedFile = null;
      this.fileList = [];
      // 清除上传组件的文件列表
      if (this.$refs.fileUpload && this.$refs.fileUpload.clearFiles) {
        this.$refs.fileUpload.clearFiles();
      }
      // 同步清空从文件自动填充的信息
      this.form.name = '';
      this.form.description = '';
    },

    // 图片上传前处理
    beforeImageUpload(file) {
      const isValidType = this.acceptedImageTypes.split(',').some(type => 
        file.name.toLowerCase().endsWith(type.replace('.', ''))
      );
      
      if (!isValidType) {
        this.$message.error('图片格式不支持！');
        return false;
      }
      
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        this.$message.error('图片大小不能超过 5MB！');
        return false;
      }
      
      return true;
    },

    // 图片选择变化
    onImageChange(file, fileList) {
      if (file.status === 'ready') {
        if (!this.selectedImageFiles) {
          this.selectedImageFiles = [];
        }
        this.selectedImageFiles.push(file.raw);
      }
    },

    // 图片移除
    onImageRemove(file) {
      if (this.selectedImageFiles) {
        const index = this.selectedImageFiles.findIndex(f => f.name === file.name);
        if (index > -1) {
          this.selectedImageFiles.splice(index, 1);
        }
      }
    },

    // 处理上传按钮点击
    handleUploadButtonClick(event) {
      event.preventDefault();
      event.stopPropagation();
      const uploadRef = this.$refs.fileUpload;
      if (uploadRef) {
        // 如果有已选择的文件，先清除文件列表，以便重新选择
        if (this.selectedFile && uploadRef.clearFiles) {
          uploadRef.clearFiles();
        }
        // 触发文件选择对话框
        const inputEl = uploadRef.$refs?.input || uploadRef.$el?.querySelector('input[type="file"]');
        if (inputEl) {
          // 重置input值，允许选择相同文件
          inputEl.value = '';
          inputEl.click();
        }
      }
    },

    // 提交表单
    async handleSubmit() {
      try {
        await this.$refs.fragmentForm.validate();
        
        if (this.mode === 'add' && !this.parentCode) {
          this.$message.error('缺少父文件编码，无法新增片段');
          return;
        }
        
        if (this.mode === 'add' && !this.selectedFile) {
          this.$message.error('请先选择文件！');
          return;
        }
        
        this.submitting = true;
        
        // 构建FormData
        const formData = new FormData();
        formData.append('name', this.form.name);
        formData.append('describes', this.form.description);
        formData.append('remark', this.form.remark || '');
        formData.append('author', this.form.author || '');
        formData.append('parentCode', this.parentCode || '');
        if (this.form.price !== null && this.form.price !== undefined) {
          formData.append('price', this.form.price);
        }
        if (this.form.page !== null && this.form.page !== undefined) {
          formData.append('pageCount', this.form.page);
        }
        if (this.form.tagId) {
          formData.append('tag1Id', this.form.tagId);
        }
        
        // PPT类型特有字段
        if (this.fileType === 1) {
          if (this.form.groupId) {
            formData.append('tag2Id', this.form.groupId);
          }
          if (this.form.animation !== null && this.form.animation !== undefined) {
            formData.append('animation', this.form.animation);
          }
        }
        
        // 文件上传
        if (this.selectedFile) {
          formData.append('file', this.selectedFile);
        }
        
        // 详情图上传（仅新增模式）
        if (this.mode === 'add' && this.selectedImageFiles && this.selectedImageFiles.length > 0) {
          this.selectedImageFiles.forEach((file) => {
            formData.append('previewFiles', file);
          });
        }
        
        let response;
        if (this.mode === 'add') {
          response = await api.createFragment(formData);
        } else {
          response = await api.updateFragment({ code: this.form.code, data: formData });
        }

        if (response.data && response.data.code === 200) {
          this.$message.success(this.mode === 'add' ? '片段添加成功！' : '片段修改成功！');
          this.$emit('form-success');
          if (this.mode === 'add') {
            this.resetForm();
          }
        } else {
          this.$message.error(response.data?.msg || '提交失败！');
        }
        
      } catch (error) {
        if (error !== false) {
          this.$message.error('提交失败！');
        }
      } finally {
        this.submitting = false;
      }
    },

    // 取消表单
    handleCancel() {
      this.$emit('form-cancel');
    },

    // 重置表单
    resetForm() {
      this.initFormData();
      if (this.$refs.fragmentForm) {
        this.$refs.fragmentForm.resetFields();
      }
    }
  }
};
</script>

<style scoped>
.fragment-form-container {
  padding: 20px;
  background: #fff;
}

.fragment-form {
  max-width: 100%;
}

.form-row {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  align-items: flex-start;
}

.file-upload-row .file-info-item .el-form-item__content {
  margin-left: 0;
  display: flex;
  justify-content: center;
}


.form-item-half {
  flex: 1;
  min-width: 0;
  margin-bottom: 0;
}

.form-item-full {
  width: 100%;
  margin-bottom: 0;
}

.description-right-column {
  display: flex;
  flex-direction: column;
  gap: 0;
  align-items: stretch;
}

.description-right-column .form-item-full {
  margin-bottom: 20px;
  width: 100%;
}

.description-right-column .form-item-full:last-child {
  margin-bottom: 0;
}

/* 简介文本框样式优化 */
.description-item {
  position: relative;
}

.description-item .el-form-item__content {
  position: relative;
}

.description-textarea {
  width: 100%;
}

/* 统一PPT和Word的描述框高度 */
.description-textarea .el-textarea__inner {
  min-height: 110px;
  padding-bottom: 25px;
}

/* 修复字数提示超出文本区域的问题 */
.description-item :deep(.el-input__count) {
  position: absolute;
  bottom: 5px;
  right: 10px;
  background: transparent;
  line-height: 1;
  color: #909399;
  font-size: 12px;
  z-index: 1;
}

/* 备注文本框字数提示样式 */
.fragment-form .form-item-full .el-textarea__inner {
  padding-bottom: 25px;
}

.fragment-form .form-item-full :deep(.el-input__count) {
  position: absolute;
  bottom: 5px;
  right: 10px;
  background: transparent;
  line-height: 1;
  color: #909399;
  font-size: 12px;
}

/* 确保简介和右侧列的垂直对齐 */
.form-row .form-item-half:first-child .el-textarea .el-textarea__inner {
  min-height: 110px;
}

.fragment-form .el-form-item {
  margin-bottom: 0;
}

.fragment-form .el-form-item__label {
  font-weight: 500;
  color: #333;
  line-height: 32px;
}

.fragment-form .el-form-item__content {
  line-height: 32px;
}

.fragment-form .el-form-item.is-required .el-form-item__label::before {
  content: '*';
  color: #f56c6c;
  margin-right: 4px;
}

.fragment-form .el-input,
.fragment-form .el-select,
.fragment-form .el-textarea,
.fragment-form .el-input-number {
  width: 100%;
}

/* 文件上传区域 */
.upload-wrapper {
  width: 100%;
  max-width: 280px;
  height: 140px;
  position: relative;
}

.upload-box {
  width: 280px;
  height: 140px;
  border: 2px dashed #d9d9d9;
  border-radius: 6px;
  position: absolute;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  transition: border-color 0.3s;
  background: #fff;
}

.upload-box:hover {
  border-color: #409eff;
}

.upload-dragger {
  width: 100%;
  height: 100%;
}

:deep(.upload-dragger .el-upload) {
  width: 100%;
  height: 100%;
  display: block;
}

:deep(.upload-dragger .el-upload-dragger) {
  width: 100%;
  height: 100%;
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
}

.upload-content {
  width: 100%;
  height: 100%;
  position: relative;
  text-align: center;
  box-sizing: border-box;
  padding: 8px;
}

.upload-inner {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  padding: 0 12px;
  box-sizing: border-box;
}

.upload-icon {
  font-size: 24px;
  color: #c0c4cc;
  margin-bottom: 6px;
  display: block;
}

.upload-tip-text {
  color: #909399;
  font-size: 12px;
  line-height: 1.4;
  margin-bottom: 8px;
}

.upload-btn {
  margin-top: 6px;
}

.file-info-card {
  max-width: 305px;
  height: 140px;
  padding: 8px 10px;
  border-radius: 6px;
  background-color: #f8f9fb;
  border: 1px solid #e4e7ed;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.file-info-card.is-placeholder {
  justify-content: center;
  align-items: center;
  text-align: center;
  color: #a0a3b1;
  gap: 6px;
}

.file-info-name {
  font-size: 13px;
  color: #303133;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-bottom: 6px;
  text-align: center;
  width: 100%;
  max-width: 100%;
}

.file-info-meta {
  font-size: 12px;
  color: #909399;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;
}

.file-info-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 12px;
  color: #909399;
}

.file-info-action {
  padding: 4px 12px;
  font-size: 12px;
  border-radius: 6px;
}

.file-info-action.el-button--primary.is-plain {
  border-color: #b3d8ff;
  color: #409eff;
  background-color: #f4f9ff;
}

.file-info-action.el-button--primary.is-plain:hover,
.file-info-action.el-button--primary.is-plain:focus {
  color: #2d8cf0;
  border-color: #90c2ff;
  background-color: #e8f3ff;
}

.file-info-action.el-button--danger.is-plain {
  border-color: #ffb8b2;
  color: #f56c6c;
  background-color: #fff3f2;
}

.file-info-action.el-button--danger.is-plain:hover,
.file-info-action.el-button--danger.is-plain:focus {
  color: #e45a5a;
  border-color: #ffa39e;
  background-color: #ffe8e6;
}

.divider {
  color: #dcdfe6;
  font-size: 12px;
  user-select: none;
}

.file-info-placeholder-icon {
  font-size: 26px;
  color: #c0c4cc;
}

.file-info-placeholder-text {
  font-size: 14px;
  color: #606266;
}

.file-info-placeholder-tip {
  font-size: 12px;
  color: #909399;
}

/* 图片上传区域 */
.image-upload {
  width: 100%;
}

.image-upload .el-upload--picture-card {
  width: 100px;
  height: 100px;
  line-height: 100px;
}

.image-upload .el-upload-list--picture-card .el-upload-list__item {
  width: 100px;
  height: 100px;
}

.upload-tip {
  color: #909399;
  font-size: 12px;
  margin-top: 10px;
  line-height: 1.4;
}

/* 按钮区域 */
.form-actions {
  text-align: center;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #f0f0f0;
}

.form-actions .el-button {
  min-width: 100px;
  margin: 0 10px;
}

/* 响应式适配 */
@media (max-width: 768px) {
  .form-row {
    flex-direction: column;
    gap: 15px;
  }
  
  .form-item-half {
    width: 100%;
  }
  
  .upload-wrapper {
    max-width: 100%;
  }
}
</style>

